import type { Chip, MarketOutlookDTO } from "@eu.mdotm.api/sphere";
import DataSeriesModal from "$root/components/DataSeriesModal";
import GraphLegend from "$root/components/GraphLegend";
import ColoredRectangle from "$root/components/icons/ColoredRectangle";
import DashedRectangle from "$root/components/icons/DashedRectangle";
import { ReactQueryWrapperBase } from "$root/components/ReactQueryWrapper";
import { useEventBus } from "$root/event-bus";
import type { SentimentType } from "$root/functional-areas/market-view/analysis/sentiment";
import { SentimentBadge } from "$root/functional-areas/market-view/analysis/SentimentBadge";
import { outlookRepository } from "$root/functional-areas/outlook/repository";
import TimeSeriesModal from "$root/functional-areas/outlook/TimeSeriesModal";
import {
	composeOutlookLink,
	useDriverComposer,
	usePositionComposer,
} from "$root/functional-areas/outlook/useOutlookComposer";
import useOutlookComposition from "$root/hooks/useOutlookComposition";
import useWidgetsData from "$root/hooks/useWidgetsData";
import { PaletteColors } from "$root/styles/themePalette";
import { Intercom } from "$root/third-party-integrations/initIntercom";
import { type ContextContent, withContext } from "$root/utils";
import { useMappedQuery } from "$root/third-party-integrations/react-query/hooks";
import { UniverseFiltersContext } from "$root/widgets-architecture/contexts/filters";
import { useWidgetOptions } from "$root/widgets-architecture/layout/WidgetsMapper/context";
import {
	type OrderBy,
	type TableColumn,
	ActionText,
	AutoSortHScrollTable,
	CircularProgressBar,
	Row,
	TableDataCell,
	TooltipContent,
} from "@mdotm/shadcn/legacy/components";
import { ForEach } from "@mdotm/utils/react-extra";
import { builtInSortFnFor, noop } from "@mdotm/shadcn/legacy/utils";
import { useCallback, useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";
import SpiderwebChart from "./CustomChart";
import { feelsConverter } from "$root/functional-areas/market-view/analysis/feels-converter";

type PositioningTableProps = {
	positioning: string;
	driver: Chip[];
	geo: string;
	sector: string;
	label: string;
	assetClass?: string;
	previousPositioning?: string;
};

type PositioningSpiderwebChartProps = {
	previous: number[];
	current: number[];
	benchmarks: number[];
	assetClasses: string[];
	dates: { [key: string]: Array<string | undefined> };
};

const Positioning = (props: ContextContent<typeof UniverseFiltersContext>) => {
	const { firstFilter, secondFilter } = props;
	const { assetClass, geography } = useOutlookComposition(firstFilter, secondFilter, "");
	const { t } = useTranslation();
	const assetClassLabels = useMemo(() => t("ASSET_CLASS_LABEL", { returnObjects: true }), [t]);
	const { TYPE_DICT } = useDriverComposer();
	const { positioningAreaByLabel, positioningAreaByValue } = usePositionComposer();

	//DTO

	const positioningTableHeader: Record<string, string> = useMemo(
		() => ({
			FI: t("OUTLOOK.TYPE"),
			CO: t("OUTLOOK.TYPE"),
			MARKETS: t("OUTLOOK.GEOGRAPHY"),
			EU: t("OUTLOOK.SECTOR"),
			US: t("OUTLOOK.SECTOR"),
		}),
		[t],
	);

	const { currentWidgetsData } = useWidgetsData();
	const dateRange = new Date(Date.parse(currentWidgetsData.creation_time.toString()));
	dateRange.setFullYear(dateRange.getFullYear() - 1);
	dateRange.setHours(0, 0, 0, 0);

	const { data: assetClassTimeSeries } = useMappedQuery(
		(outlookTimeSeries) => {
			return outlookTimeSeries.reduce<{ [key: string]: [number, number, boolean][] }>((acc, el) => {
				const { type, values, inverted } = el;
				if (!type || !values) {
					return acc;
				}

				acc[type] = values
					.filter(([_x, y]) => y ?? false)
					.map(([dateSeconds, y]) => [dateSeconds * 1000, y, inverted]) as [number, number, boolean][];
				return acc;
			}, {});
		},
		[],
		outlookRepository.outlookTimeSeriesContinuousByAssetClass.useQuery({
			assetClass,
			geography,
			fromDate: dateRange.toISOString().split("T")[0],
		}),
	);

	// section: React Query
	const composePositioningTableData = useCallback(
		(data: MarketOutlookDTO[]) => {
			return data.reduce<PositioningTableProps[]>(
				(acc, { type, currentValue, driverLabel, geography: geographyCur, sector, prevValue }) => [
					...acc,
					{
						label: assetClassLabels[type as keyof typeof assetClassLabels] ?? "-",
						assetClass: type,
						positioning: currentValue?.value ? positioningAreaByValue[currentValue.value] : "-",
						previousPositioning: prevValue?.value ? positioningAreaByValue[prevValue?.value] : undefined,
						driver: driverLabel ?? [],
						geo: geographyCur ?? "",
						sector: sector ?? "",
					},
				],
				[],
			);
		},
		[assetClassLabels, positioningAreaByValue],
	);

	const composePositioningSpiderwebChart = useCallback(
		(data: MarketOutlookDTO[]) => {
			const { current, previous, benchmarks, assetClasses, dates } = data.reduce<PositioningSpiderwebChartProps>(
				(
					{
						previous: previousAcc,
						current: currentAcc,
						assetClasses: assetClassesAcc,
						benchmarks: benchmarksAcc,
						dates: datesAcc,
					},
					{ currentValue, prevValue, type },
				) => ({
					previous: [...previousAcc, (prevValue?.value || currentValue?.value) ?? 0],
					current: [...currentAcc, currentValue?.value ?? 0],
					assetClasses: [
						...assetClassesAcc,
						(assetClassLabels[type as keyof typeof assetClassLabels] ?? "-").toUpperCase(),
					],
					benchmarks: [...benchmarksAcc, 3],
					dates: {
						...datesAcc,
						[(assetClassLabels[type as keyof typeof assetClassLabels] ?? "-").toUpperCase()]: [
							currentValue?.data,
							prevValue?.data,
						],
					},
				}),
				{ previous: [], current: [], assetClasses: [], benchmarks: [], dates: {} },
			);

			return {
				series: [
					{
						name: "Current",
						data: current,
						pointPlacement: "on",
						color: PaletteColors.AZURE,
						marker: {
							enabled: false,
						},
						zIndex: 5,
					},
					{
						name: "Previous",
						data: previous,
						pointPlacement: "on",
						color: PaletteColors.BLUEY_GREY,
						marker: {
							enabled: false,
						},
					},
					{
						name: "Benchmark",
						data: benchmarks,
						pointPlacement: "on",
						color: PaletteColors.BLUEY_GREY,
						dashStyle: "Dash",
						marker: {
							enabled: false,
							states: {
								hover: {
									enabled: false,
								},
							},
						},
						opacity: 1,
					},
				],
				assetClasses,
				dates,
			};
		},
		[assetClassLabels],
	);

	const outlookByAssetClassQuery = outlookRepository.outlookByAssetClass.useQuery({
		assetClass,
		geography,
	});
	const queryOverUnderWeightPositioning = useMappedQuery(
		(outlook) => {
			const rows = composePositioningTableData(outlook);
			const { series, assetClasses, dates } = composePositioningSpiderwebChart(outlook);

			return { rows, series, assetClasses, dates };
		},
		[composePositioningSpiderwebChart, composePositioningTableData],
		outlookByAssetClassQuery,
	);

	const { data, refetch } = queryOverUnderWeightPositioning;

	const { assetClasses, series, rows, dates } = data ?? {};

	useEventBus("market-update", () => {
		refetch().catch(noop);
	});

	const columns = useMemo<TableColumn<PositioningTableProps>[]>(
		() => [
			{
				header: secondFilter ? positioningTableHeader[secondFilter] : positioningTableHeader[firstFilter],
				content: ({ label, sector, geo }, cellProps) => {
					const to = composeOutlookLink(firstFilter, secondFilter, sector, geo);

					return (
						<TableDataCell {...cellProps}>
							<ActionText type="Body/M/Bold" href={to} title={label}>
								{label}
							</ActionText>
						</TableDataCell>
					);
				},
				minWidth: 180,
				sortFn: builtInSortFnFor("label"),
				name: "label",
			},
			{
				header: t("OUTLOOK.POSITIONING"),
				cellClassName: "w-full",
				content: (p, cellProps) => {
					const { positioning, previousPositioning } = p;
					const positioningSentiment: Record<string, SentimentType> = {
						[t("STRONG_UNDERWEIGHT")]: "super-negative",
						[t("UNDERWEIGHT")]: "negative",
						[t("NEUTRAL")]: "neutral",
						[t("OVERWEIGHT")]: "positive",
						[t("S_OVERWEIGHT")]: "super-positive",
					};

					if (!positioningSentiment[positioning]) {
						return null;
					}
					const disabled =
						assetClassTimeSeries?.[p.assetClass ?? ""] === undefined ||
						assetClassTimeSeries[p.assetClass ?? ""].length === 0;

					return (
						<Row justifyContent="space-between" alignItems="center" gap={8} {...cellProps}>
							<SentimentBadge sentiment={positioningSentiment[positioning]} indicator="positioning">
								{positioning}
							</SentimentBadge>
							<TimeSeriesModal
								assetClass={p.assetClass ?? ""}
								currentRegime={{ value: positioningAreaByLabel[positioning], label: positioning }}
								previousRegime={{
									value: previousPositioning
										? positioningAreaByLabel[previousPositioning]
										: positioningAreaByLabel[positioning],
									label: previousPositioning ?? "",
								}}
								series={assetClassTimeSeries}
								disabled={disabled}
							/>
						</Row>
					);
				},
				minWidth: 160,
				sortFn: builtInSortFnFor("positioning"),
				name: "positioning",
			},
			{
				name: "driver",
				header: t("OUTLOOK.WHY"),
				content: ({ driver: drivers }, cellProps) => (
					<Row gap={2} alignItems="center" {...cellProps}>
						<ForEach
							collection={drivers.flatMap((x) => {
								const feel = feelsConverter(x);
								return feel ? [feel] : [];
							})}
						>
							{(item) => (
								<SentimentBadge sentiment={item.sentiment} indicator={item.indicator}>
									{item.label}
								</SentimentBadge>
							)}
						</ForEach>
					</Row>
				),
				minWidth: 380,
			},
		],
		[secondFilter, positioningTableHeader, firstFilter, t, positioningAreaByLabel, assetClassTimeSeries],
	);
	// section: Widget context
	useWidgetOptions(
		() => ({
			tooltip: (
				<TooltipContent>
					<Trans
						t={t}
						i18nKey="POSITIONING_TOOLTIP"
						components={{
							s: (
								<>
									<br />
									<br />
								</>
							),
							l: (
								<button
									type="button"
									className="underline cursor-pointer font-bold"
									onClick={() => Intercom.showArticle("GeographicAndSectorIndicators")}
								>
									Button
								</button>
							),
						}}
					/>
				</TooltipContent>
			),
			actionHeader: <DataSeriesModal firstFilter={firstFilter} secondFilter={secondFilter} typeMode="OUTLOOK" />,
			title: t("POSITIONING"),
		}),
		[t, firstFilter, secondFilter],
	);

	return (
		<ReactQueryWrapperBase
			query={queryOverUnderWeightPositioning}
			loadingFallback={<CircularProgressBar value="indeterminate" className="m-auto" />}
		>
			{() => (
				<>
					<div className="grid grid-cols-5 h-full overflow-hidden">
						<div className="col-span-2 h-96 relative flex items-center justify-center">
							<SpiderwebChart
								xAxis={assetClasses ?? []}
								series={series ?? []}
								representing={`${firstFilter}${secondFilter}`}
								notInTooltip={["Benchmark"]}
								valueToTooltip={TYPE_DICT}
								dates={dates ?? {}}
							/>
						</div>
						<div className="col-span-3 h-full min-h-0 flex flex-col">
							<AutoSortHScrollTable
								columns={columns}
								rows={rows ?? []}
								orderBy={defaultPositionOrderBy}
								noDataText={
									<div className="flex items-center justify-center h-20">
										<p className="bg-transparent!">No data available</p>
									</div>
								}
							/>
						</div>
					</div>
					<GraphLegend>
						<div className="legend-container">
							<ColoredRectangle /> {t("CURRENT", { returnObjects: false })}
						</div>
						<div className="legend-container">
							<ColoredRectangle color={PaletteColors.BLUEY_GREY} /> {t("PREVIOUS")}
						</div>
						<div className="legend-container">
							<DashedRectangle /> {t("NEUTRAL")}
						</div>
					</GraphLegend>
				</>
			)}
		</ReactQueryWrapperBase>
	);
};

const defaultPositionOrderBy: OrderBy[] = [{ columnName: "label", direction: "asc" }];

export default withContext(UniverseFiltersContext)(Positioning);
